import { defineStore } from 'pinia'

export const AssetStore = defineStore('assets', {
  state: () => {
    return {
      videos: JSON.parse(localStorage.getItem("videos") || "[]"),
      groups: JSON.parse(localStorage.getItem("groups") || "[]"),
      series: JSON.parse(localStorage.getItem("series") || "[]"),
      tags: JSON.parse(localStorage.getItem("tags") || "[]"),
      chapters: JSON.parse(localStorage.getItem("chapters") || "[]"),
      assets: JSON.parse(localStorage.getItem("assets") || "[]"),
      clips: JSON.parse(localStorage.getItem("clips") || "[]"),
      images: JSON.parse(localStorage.getItem("images") || "[]"),
      articles: JSON.parse(localStorage.getItem("articles") || "[]"),
      audios: JSON.parse(localStorage.getItem("audios") || "[]"),
      sources: JSON.parse(localStorage.getItem("sources") || "[]"),
      documents: JSON.parse(localStorage.getItem("documents") || "[]"),
      lists: JSON.parse(localStorage.getItem("lists") || "[]"),
      recurringExports: JSON.parse(localStorage.getItem("recurringExports") || "[]"),
      exports: [],
      interactions: [],
      loading: false,
      selected: null,
      currentSrc: null,
      currentList: null
    }
  },

  actions: {
    async index(from = "empty") {
      log.info(5,"🌜 AssetStoreIndex", from)
      this.loading = true
      return this.Api.get('/assets').then(response => {
        // log.info(5,"🌜 AssetStore index", response)
        this.videos = this.videos.length > 0 ? this.videos : response.data.videos
        this.groups = response.data.groups
        this.series = response.data.series
        this.tags = response.data.tags
        this.chapters = response.data.chapters
        this.assets = response.data.assets
        this.images = response.data.images
        this.clips = response.data.clips
        this.articles = response.data.articles
        this.files = response.data.files
        this.countries = response.data.countries
        this.audios = response.data.audios
        this.sources = response.data.sources
        this.lists = response.data.lists
        this.documents = response.data.documents
        this.recurringExports = response.data.recurring_exports
        this.storeData(response.data)
      }).finally(() => {
        this.loading = false
      })
    },
    async indexExports() {
      log.info(5,"🌜 AssetStore indexExports")
      return this.Api.get('/reporting/exports').then(response => {
        log.info(5,"🌜 AssetStore indexExports", response)
        this.exports = response.data.exports
      })
    },
    // Add this debugging to your AssetStore to track what's happening
    async indexVideos() {
      log.info(5,"🌜 AssetStore indexVideos START")

      // Check cached videos structure
      const cachedVideos = JSON.parse(localStorage.getItem("videos") || "[]")
      if (cachedVideos.length > 0) {
        log.info(5,"📦 CACHE: Found", cachedVideos.length, "videos")
        log.info(5,"📦 CACHE: Sample video keys:", Object.keys(cachedVideos[0]))
        log.info(5,"📦 CACHE: Sample stats:", {
          views: cachedVideos[0].stats_views,
          interactions: cachedVideos[0].stats_interactions,
          viewers: cachedVideos[0].stats_viewers
        })

        // Set cached data immediately
        this.videos = cachedVideos
        log.info(5,"📦 CACHE: Videos set to cached data")
      }

      try {
        log.info(5,"🌐 API: Starting paginated background fetch...")
        let allVideos = []
        let offset = 0
        const limit = 100
        let hasMoreVideos = true
        
        while (hasMoreVideos) {
          log.info(5,`🌐 API: Fetching videos batch: offset=${offset}, limit=${limit}`)
          
          const response = await this.Api.get(`/videos?limit=${limit}&offset=${offset}`)
          const batchVideos = response.data.videos
          
          log.info(5,`🌐 API: Batch received: ${batchVideos.length} videos`)
          
          // Add the new batch to our collection
          allVideos = allVideos.concat(batchVideos)
          
          // Check if we've loaded all videos (batch size less than limit means we're done)
          if (batchVideos.length < limit) {
            hasMoreVideos = false
            log.info(5,`🌐 API: All videos loaded. Total: ${allVideos.length} videos`)
          } else {
            offset += limit
          }
        }

        log.info(5,"🌐 API: Sample video keys:", allVideos.length > 0 ? Object.keys(allVideos[0]) : "No videos")
        if (allVideos.length > 0) {
          log.info(5,"🌐 API: Sample stats:", {
            views: allVideos[0].stats_views,
            interactions: allVideos[0].stats_interactions,
            viewers: allVideos[0].stats_viewers
          })
        }

        // Check if data actually changed
        const hasChanged = JSON.stringify(this.videos) !== JSON.stringify(allVideos)
        log.info(5,"🔄 DATA: Changed since cache?", hasChanged)

        // Update videos
        this.videos = allVideos
        log.info(1,"🌐 API: Videos updated", allVideos.length)
        localStorage.setItem("videos", JSON.stringify(allVideos))
        log.info(5,"🌐 API: Videos updated silently")

      } catch (error) {
        log.info(1,"❌ API: Failed to fetch videos:", error)
      }
    },
    findVideo(id) {
      if (!id) {
        return null
      }
      if (id.includes("---")) {
        return this.videos.find(video => video.token == id.split("---")[1])
      } else {
        return this.videos.find(video => video.id == id)
      }
    },
    findSource(id) {
      return this.sources.find(source => source.id == id)
    },
    findClip(id) {
      return this.clips.find(clip => clip.id == id)
    },
    findAudio(id) {
      return this.audios.find(asset => asset.id == id)
    },
    findFile(id) {
      return this.files.find(asset => asset.id == id)
    },
    findArticle(id) {
      return this.articles.find(asset => asset.id == id)
    },
    findGroup(id) {
      return this.groups.find(group => group.id == id)
    },
    async getAssetFromKey(key) {
      return this.Api.get('/assets/key?key=' + key).then(response => {
        log.info(5,"🌜 AssetStore getSourceFromKey", response)
        return response.data
      })
    },
    async getArticle(id) {
      log.info(5,"🌜 AssetStore getArticle", id)
      let article = this.articles.find(asset => asset.id == id)
      if (!article) {
        const response = await this.Api.get('/articles/' + id)
        log.info(5,"🌜 AssetStore getArticle", response)
        article = response.data.article
        log.info(5,"🌜 AssetStore return article", article)
        return article
      } else {
        return article
      }
    },
    clearData() {
      log.info(5,"🌜 AssetStore clearData")
      this.videos = []
      this.groups = []
      this.series = []
      this.tags = []
      this.chapters = []
      this.assets = []
      this.images = []
      this.clips = []
      this.articles = []
      this.countries = []
      this.files = []
      this.lists = []
      this.recurringExports = []
      this.exports = []
      this.interactions = []
      localStorage.removeItem("videos")
      localStorage.removeItem("groups")
      localStorage.removeItem("series")
      localStorage.removeItem("tags")
      localStorage.removeItem("chapters")
      localStorage.removeItem("assets")
      localStorage.removeItem("images")
      localStorage.removeItem("clips")
      localStorage.removeItem("articles")
      localStorage.removeItem("countries")
      localStorage.removeItem("files")
      localStorage.removeItem("lists")
      localStorage.removeItem("recurringExports")

    },
    storeData(data) {
      localStorage.setItem("videos", JSON.stringify(data.videos || []))
      localStorage.setItem("groups", JSON.stringify(data.groups || []))
      localStorage.setItem("series", JSON.stringify(data.series || []))
      localStorage.setItem("tags", JSON.stringify(data.tags || []))
      localStorage.setItem("chapters", JSON.stringify(data.chapters || []))
      localStorage.setItem("assets", JSON.stringify(data.assets || []))
      localStorage.setItem("images", JSON.stringify(data.images || []))
      localStorage.setItem("clips", JSON.stringify(data.clips || []))
      localStorage.setItem("articles", JSON.stringify(data.articles || []))
      localStorage.setItem("countries", JSON.stringify(data.countries || []))
      localStorage.setItem("files", JSON.stringify(data.files || []))
      localStorage.setItem("lists", JSON.stringify(data.lists || []))
      localStorage.setItem("recurringExports", JSON.stringify(data.recurringExports || []))
    },
    getInteractions(token) {
      return this.Api.get('/annotations/' + token).then(response => {
        log.info(5,"🌜 AssetStore getInteractions", response)
        return response.data.annotations
      })
    },
    createAsset(file, title = null) {
      return this.Api.post('/assets', { file: file, title: title }).then(response => {
        log.info(5,"🌜 AssetStore created", response)
        // this.index()
        return response
      })
    },
    updateAsset(id, data) {
      return this.Api.put('/assets/' + id, data).then(response => {
        log.info(5,"🌜 AssetStore updateAsset", response)
        this.index()
        return response
      })
    },
    hideAsset(id) {
      return this.Api.post('/assets/hide', { id: id }).then(response => {
        log.info(5,"🌜 AssetStore hideAsset", response)
        this.index()
        return response
      })
    },
    updateArticle(id, data) {
      return this.Api.put('/articles/' + id, data).then(response => {
        log.info(5,"🌜 AssetStore updateArticle", response)
        this.index()
        return response
      })
    },
    createArticle(data) {
      return this.Api.post('/articles', data).then(response => {
        log.info(5,"🌜 AssetStore createArticle", response)
        this.index()
        return response
      })
    },
    streamById(id) {
      this.currentSrc = null
      return this.Api.get('/assets/stream/' + id).then(response => {
        log.info(5,"🌜 AssetStore streamById", response)
        this.currentSrc = response.data.url
        return response.data.url
      })
    },
    fetchList(id) {
      return this.Api.get('/access-lists/' + id).then(response => {
        log.info(5,"🌜 AssetStore fetchList", response)
        this.currentList = response.data.access_list
      })
    },
    addListItem(entry) {
      return this.Api.post('/access-lists/' + this.currentList.id + '/items', { item: entry })
    },
    removeListItem(id) {
      log.info(5,"🌜 AssetStore removeListItem", id)
      return this.Api.destroy('/access-lists/' + this.currentList.id + '/items/' + id)
    },
    updateList(id, data) {
      return this.Api.put('/access-lists/' + id, data)
    },
    createList(data) {
      return this.Api.post('/access-lists', data)
    },
    deleteList(id) {
      return this.Api.destroy('/access-lists/' + id)
    },
    addListItems(items) {
      return this.Api.post('/access-lists/' + this.currentList.id + '/items/bulk', { items: items })
    },
    importInteractions(token, interactionIds) {
      log.info(5,"🌜 AssetStore importInteractions", token, interactionIds)
      return this.Api.post('/annotations/import', { token: token, interaction_ids: interactionIds })
    },
    async indexExports() {
      return this.Api.get('/reporting/exports').then(response => {
        this.exports = response.data.exports
      })
    },
    async indexRecurringExports() {
      return this.Api.get('/reporting/exports/recurring').then(response => {
        this.recurringExports = response.data.exports
      })
    },
    async getExport(id) {
      return this.Api.get(`/reporting/exports/${id}`).then(response => {
        return response.data.export;
      }).finally(() => {
        this.loading = false
      })
    },

    async getRecurringExport(id) {
      return this.Api.get(`/reporting/exports/recurring/${id}`).then(response => {
        return response.data.export;
      }).finally(() => {
        this.loading = false
      })
    },

    async createRecurringExport(data) {
      return this.Api.post('/reporting/exports/recurring', data).then(response => {
        log.info(5,"🌜 AssetStore createRecurringExport", response)
        this.indexRecurringExports()
        return response
      })
    },
    updateRecurringExport(data) {
      return this.Api.put(`/reporting/exports/recurring/${data.id}`, data).then(response => {
        log.info(5,"🌜 AssetStore updateRecurringExport", response)
        this.indexRecurringExports()
        return response
      })
    },
    deleteRecurringExport(id) {
      return this.Api.destroy(`/reporting/exports/recurring/${id}`).then(response => {
        log.info(5,"🌜 AssetStore deleteRecurringExport", response)
        this.indexRecurringExports()
        return response
      })
    }
  },
  getters: {
    titleSortedVideos: (state) => {
      return state.videos.sort((a, b) => a.title.localeCompare(b.title));
    },
    dateSortedVideos: (state) => {
      return state.videos.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
  }
})